/**
 * Remembers where an unauthenticated user was trying to go so we can send them
 * back there after sign-in (e.g. an "upgrade" link to /dashboard/settings?tab=subscription).
 *
 * Only same-origin relative paths are accepted. Protocol-relative values
 * ("//evil.com") and auth pages are rejected so this can never become an open redirect.
 */
const STORAGE_KEY = 'redirectUrl';

// Sign-in triggers more than one redirect (the SignIn form and the Auth page's
// auth-state listener both fire). The first caller consumes storage; callers in
// the next few seconds get the same answer so both redirects agree.
const CACHE_TTL_MS = 5000;
let cached: { url: string | null; at: number } | null = null;

function isSafeRedirect(url: string | null): url is string {
  if (!url) return false;
  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) return false;
  if (url.startsWith('/auth')) return false;
  return true;
}

export function saveRedirectUrl(url: string): void {
  if (!isSafeRedirect(url)) return;
  cached = null;
  try {
    sessionStorage.setItem(STORAGE_KEY, url);
  } catch {
    // Storage unavailable (private mode, blocked) — fall back to the default destination.
  }
}

export function consumeRedirectUrl(): string | null {
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.url;
  let url: string | null = null;
  try {
    url = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    url = null;
  }
  cached = { url: isSafeRedirect(url) ? url : null, at: Date.now() };
  return cached.url;
}
